import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useAgentStateDiff,
  useAgentStateDocument,
  useAgentStateList,
} from "../api";
import { DiffView } from "../components/DiffView";
import { runLinkPath } from "./learningAgentPaths";
import styles from "./ProjectAgentStateTab.module.css";
import { CitedBody } from "../components/CitedBody";
import { useCitations } from "../components/useCitations";
import { WrittenBeforeBranch } from "../components/AgentSlotBar";

// Read-only by design: agents write state documents, operators review them. Being
// able to see every revision and diff any two is the trust mechanism for
// auto-accepted agent writes.

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// De-underscored section ids read as the headings below; repeats collapse.
const changedSections = (ids: string[]): string => {
  if (ids.length === 0) return "no sections recorded";
  return [...new Set(ids)].map((id) => id.replace(/_/g, " ")).join(", ");
};

// /projects/:id/learning-agents/state/:agent - one agent's document, named in the URL.
export const ProjectAgentStateRoute = () => {
  const { agent } = useParams();
  return <ProjectAgentStateTab onlyAgent={agent} />;
};

// Also the agent's GLOBAL state page, which differs only in scope.
export const ProjectAgentStateTab = ({
  scope,
  onlyAgent,
}: {
  // The state scope to read, when it is not the project in the URL.
  scope?: string;
  // Show one agent rather than the switcher, for a page whose URL names it.
  onlyAgent?: string;
} = {}) => {
  const { projectId } = useParams();
  const decoded = scope ?? (projectId ? decodeURIComponent(projectId) : "");
  const { data: allAgents, isLoading, error } = useAgentStateList(decoded);
  const agents = useMemo(
    () => (onlyAgent ? (allAgents ?? []).filter((a) => a.agent === onlyAgent) : allAgents),
    [allAgents, onlyAgent],
  );

  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  // null means "the current document", which stays correct as new runs land.
  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const [compareFrom, setCompareFrom] = useState<number | null>(null);
  const [comparing, setComparing] = useState(false);

  const agent = useMemo(() => {
    if (!agents || agents.length === 0) return null;
    return agents.find((a) => a.agent === selectedAgent) ?? agents[0];
  }, [agents, selectedAgent]);

  // So one agent's version number can't leak into another's history.
  useEffect(() => {
    setViewVersion(null);
    setCompareFrom(null);
    setComparing(false);
  }, [agent?.agent]);

  const readVersion = viewVersion ?? agent?.latest_version ?? null;
  const { data: revision, isLoading: revisionLoading } = useAgentStateDocument(
    decoded,
    agent?.agent ?? null,
    viewVersion,
  );
  const { data: diff, isLoading: diffLoading, isError: diffError } = useAgentStateDiff(
    decoded,
    agent?.agent ?? null,
    compareFrom,
    readVersion,
    comparing,
  );
  // ONE lookup over every section's prose. A hook per section would be a request
  // per section, and a state document has a dozen of them (principle 8).
  const sectionBodies = useMemo(
    () => (revision?.sections ?? []).map((s) => s.body ?? ""),
    [revision],
  );
  const citations = useCitations(sectionBodies);

  if (error) {
    return (
      <div className={styles.empty}>
        Couldn&apos;t load agent state: {error.message}
      </div>
    );
  }
  if (isLoading) {
    return <div className={styles.empty}>Loading agent state…</div>;
  }
  if (!agents || agents.length === 0 || !agent) {
    return (
      <div className={styles.empty}>
        <h3 className={styles.emptyTitle}>No agent state yet</h3>
        <p>
          {scope
            ? `This agent keeps one document across every project it works - what
               it has learned about its counterparties and about doing the work
               itself. A new version is saved when a run that edited it
               completes.`
            : `Each agent keeps a durable state document for this project - what it
               has established, what it is waiting on, and what it has learned. A
               new version is saved when a run that edited it completes, so this
               fills in after the agents next run on this project.`}
        </p>
      </div>
    );
  }

  const versions = agent.versions;
  const currentSummary =
    versions.find((v) => v.version === readVersion) ?? versions[0];

  return (
    <div className={styles.tab}>
      {/* Hidden when the page is already about one agent - a switcher with one
          button is a control that does nothing. */}
      <div className={styles.agentBar} hidden={agents.length < 2}>
        {agents.map((a) => (
          <button
            key={a.agent}
            type="button"
            className={`${styles.agentTab} ${
              a.agent === agent.agent ? styles.agentTabActive : ""
            }`}
            onClick={() => setSelectedAgent(a.agent)}
          >
            {a.label}
            <span className={styles.agentMeta}>
              v{a.latest_version} · {formatTimestamp(a.updated_at)}
            </span>
          </button>
        ))}
      </div>

      <div className={styles.body}>
        <aside className={styles.revisions}>
          <div className={styles.revisionsHeader}>
            Revisions ({versions.length})
          </div>
          <ul className={styles.revisionList}>
            {versions.map((v) => {
              const isCurrent = v.version === readVersion;
              return (
                <li key={v.version}>
                  <button
                    type="button"
                    className={`${styles.revision} ${
                      isCurrent ? styles.revisionActive : ""
                    }`}
                    onClick={() => {
                      setViewVersion(
                        v.version === agent.latest_version ? null : v.version,
                      );
                      setComparing(false);
                    }}
                  >
                    <span className={styles.revisionTop}>
                      <span className={styles.revisionVersion}>
                        v{v.version}
                        {v.version === agent.latest_version && (
                          <span className={styles.currentPill}>current</span>
                        )}
                      </span>
                      <span className={styles.revisionDate}>
                        {formatTimestamp(v.created_at)}
                      </span>
                    </span>
                    <span className={styles.revisionSections}>
                      {changedSections(v.changed_section_ids)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className={styles.documentPane}>
          <div className={styles.docHeader}>
            <div>
              <div className={styles.docTitle}>
                {agent.label} · v{readVersion}
              </div>
              {currentSummary && (
                <div className={styles.docSub}>
                  Written {formatTimestamp(currentSummary.created_at)}
                  {currentSummary.set_by_run_id && decoded && (
                    <>
                      {" by "}
                      <Link
                        className={styles.runLink}
                        to={runLinkPath(currentSummary.set_by_run_id)}
                      >
                        this run
                      </Link>
                      <WrittenBeforeBranch at={currentSummary.created_at} />
                    </>
                  )}
                </div>
              )}
            </div>
            <div className={styles.compareControls}>
              <label className={styles.compareLabel} htmlFor="compare-from">
                Compare with
              </label>
              <select
                id="compare-from"
                className={styles.select}
                value={
                  !comparing
                    ? ""
                    : compareFrom == null
                      ? "empty"
                      : String(compareFrom)
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setComparing(false);
                    setCompareFrom(null);
                    return;
                  }
                  setCompareFrom(raw === "empty" ? null : Number(raw));
                  setComparing(true);
                }}
              >
                <option value="">(no comparison)</option>
                <option value="empty">nothing (first version)</option>
                {versions
                  .filter((v) => v.version !== readVersion)
                  .map((v) => (
                    <option key={v.version} value={String(v.version)}>
                      v{v.version} · {formatTimestamp(v.created_at)}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {comparing ? (
            diffLoading ? (
              <div className={styles.empty}>Computing diff…</div>
            ) : diffError ? (
              // Before the empty case: a failed read is not "no difference".
              <div className={styles.empty}>This diff could not be computed.</div>
            ) : !diff || diff.lines.length === 0 ? (
              <div className={styles.noChange}>
                No difference between these two revisions.
              </div>
            ) : (
              <DiffView lines={diff.lines} />
            )
          ) : revisionLoading ? (
            <div className={styles.empty}>Loading revision…</div>
          ) : !revision ? (
            <div className={styles.empty}>
              This revision could not be loaded.
            </div>
          ) : (
            <div className={styles.sections}>
              {revision.sections.map((section) => (
                <article
                  key={section.section_id}
                  className={styles.sectionCard}
                >
                  <h4 className={styles.sectionTitle}>{section.title}</h4>
                  {section.body ? (
                    <CitedBody
                      body={section.body}
                      citations={citations}
                      className={styles.sectionBody}
                    />
                  ) : (
                    <div className={styles.sectionEmpty}>
                      Nothing recorded in this section yet.
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
      {citations.modal}
    </div>
  );
};
